import React from 'react';
import { IonIcon, IonInfiniteScroll, IonInfiniteScrollContent } from '@ionic/react';
import { chevronForwardOutline, ticketOutline } from 'ionicons/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import Loading from '../../../components/spinner';
import { withTranslation } from '../../../lib/translate';
import Layout from '../../../components/layout';
import SwipableTabs from '../../../components/swipeableTabs';
import { NormalText, SmallText, Spacer, StrongText, Subtitle, Title } from '../../../components/common';
import NoData from '../../../components/noData';
import { forwardTo } from '../../../lib/utils';
import {
  getMyEventsCurrent,
  getMyEventsHistory,
  getMyEventsUpcoming,
} from '../../../store/actions';
import './index.css';

const BUCKETS = {
  myEventsCurrent: getMyEventsCurrent,
  myEventsUpcoming: getMyEventsUpcoming,
  myEventsHistory: getMyEventsHistory,
};

class TicketsHistory extends React.Component {
  loadingMore = {};

  listRefs = {
    myEventsCurrent: React.createRef(),
    myEventsUpcoming: React.createRef(),
    myEventsHistory: React.createRef(),
  };

  componentDidMount() {
    Object.values(BUCKETS).forEach((action) => this.props.dispatch(action(1)));
  }

  componentDidUpdate(prevProps) {
    Object.keys(BUCKETS).forEach((bucket) => {
      if (prevProps[bucket] !== this.props[bucket]) {
        this.loadingMore[bucket] = false;
        this.loadMoreIfNotScrollable(bucket);
      }
    });
  }

  loadMoreIfNotScrollable = async (bucket) => {
    const content = this.listRefs[bucket].current?.closest('ion-content');
    if (!content) {
      return;
    }
    const scrollEl = await content.getScrollElement();
    if (scrollEl.scrollHeight <= scrollEl.clientHeight) {
      this.loadMore(bucket);
    }
  };

  loadMore = (bucket) => {
    if (!this.props[`${bucket}HasMore`] || this.loadingMore[bucket]) {
      return;
    }
    this.loadingMore[bucket] = true;
    this.props.dispatch(BUCKETS[bucket]((this.props[`${bucket}Page`] || 1) + 1));
  };

  openEvent = (event, isUsed = false) => {
    forwardTo('/ticket', { eventId: event.id, eventName: event.name, isUsed });
  };

  getBadge = (event) => {
    const { __ } = this.props;
    if ((event.tickets || []).some((ticket) => ticket.is_current)) {
      return __('Tonight');
    }
    return event.start_time ? moment(event.start_time).format('MMM D') : null;
  };

  getSubtitle = (event) => {
    const parts = [
      event.start_time ? moment(event.start_time).format('h:mm A') : null,
      event.venue_name,
    ].filter(Boolean);
    return parts.join(' · ');
  };

  getMeta = (event) => {
    const { __ } = this.props;
    const tickets = event.tickets || [];
    const count = tickets.length;
    const first = tickets[0] || {};
    const parts = [
      count === 1 ? __('{count} ticket', { count }) : __('{count} tickets', { count }),
      first.tier?.name,
      first.id ? `${__('Ref')}: ${first.id}` : null,
    ].filter(Boolean);
    return parts.join(' · ');
  };

  getUpcomingMeta = (event) => {
    const { __ } = this.props;
    const count = (event.tickets || []).length;
    const parts = [
      event.start_time ? moment(event.start_time).format('ddd D MMM') : null,
      event.start_time ? moment(event.start_time).format('h:mma') : null,
      count === 1 ? __('{count} ticket', { count }) : __('{count} tickets', { count }),
    ].filter(Boolean);
    return parts.join(' · ');
  };

  renderTicketCard = (event, i) => {
    const { __ } = this.props;
    const badge = this.getBadge(event);
    const subtitle = this.getSubtitle(event);

    return (
      <div
        className="ticket-card"
        key={event.id != null ? `event-${event.id}` : `event-${i}`}
        onClick={() => this.openEvent(event)}
      >
        <div
          className="ticket-card-hero"
          style={event.primary_image ? { backgroundImage: `url(${event.primary_image})` } : undefined}
        >
          {badge && (
            <div className="ticket-card-badge">
              <SmallText>{badge}</SmallText>
            </div>
          )}
          <div className="ticket-card-hero-text">
            <StrongText className="block ticket-text">{event.name}</StrongText>
            {subtitle && (
              <SmallText style={{opacity:'0.7'}} className="block ticket-text">{subtitle}</SmallText>
            )}
          </div>
        </div>
        <div className="ticket-card-body">
          <div>
            <SmallText style={{opacity:'0.7'}} className="block ticket-text">{this.getMeta(event)}</SmallText>
            <Spacer size={0}/>
            <SmallText className="block ticket-text">
              {__('Tap to view QR code')} →
            </SmallText>
          </div>
          <div className="ticket-card-qr">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <rect x="3" y="3" width="5" height="5" />
              <rect x="3" y="16" width="5" height="5" />
              <rect x="16" y="3" width="5" height="5" />
              <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
              <path d="M21 21v.01" />
              <path d="M12 7v3a2 2 0 0 1-2 2H7" />
              <path d="M3 12h.01" />
              <path d="M12 3h.01" />
              <path d="M12 16v.01" />
              <path d="M16 12h1a2 2 0 0 1 2 2v1" />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  // Design for the "Upcoming" tab
  renderUpcomingTicketCard = (event, i, isUsed) => {
    return (
      <div
        className="upcoming-ticket-row pointer"
        key={event.id != null ? `upcoming-event-${event.id}` : `upcoming-event-${i}`}
        onClick={() => this.openEvent(event, isUsed)}
      >
        <div>
          <StrongText className="block">{event.name}</StrongText>
          <SmallText className="block light-color">{this.getUpcomingMeta(event)}</SmallText>
        </div>
        <IonIcon style={{ height: '18px', width: '18px' }} icon={chevronForwardOutline} />
      </div>
    );
  };

  renderInfiniteScroll = (bucket) => (
    <IonInfiniteScroll
      threshold="100px"
      disabled={!this.props[`${bucket}HasMore`]}
      onIonInfinite={(e) => {
        this.loadMore(bucket);
        e.target.complete();
      }}
    >
      <IonInfiniteScrollContent />
    </IonInfiniteScroll>
  );

  renderNoTickets = (title, text) => {
    const { __ } = this.props;
    return (
      <div className="history-content">
        <NoData className="no-tickets-page">
          <IonIcon icon={ticketOutline} />
          <Spacer size={1} />
          <Title>{__(title)}</Title>
          <Spacer size={1} />
          <NormalText>{__(text)}</NormalText>
        </NoData>
      </div>
    );
  };

  renderUpcomingList = (bucket, emptyTitle, emptyText) => {
    const events = this.props[bucket] || [];
    if (events.length === 0) {
      return this.renderNoTickets(emptyTitle, emptyText);
    }
    return (
      <div className="history-content" ref={this.listRefs[bucket]}>
        <div className="box-wrapper box-wrapper-list">
          {events.map((event, i) =>
            this.renderUpcomingTicketCard(event, i, bucket === 'myEventsHistory'),
          )}
        </div>
        {this.renderInfiniteScroll(bucket)}
      </div>
    );
  };

  renderEventList = (bucket, emptyTitle, emptyText) => {
    const events = this.props[bucket] || [];
    if (events.length === 0) {
      return this.renderNoTickets(emptyTitle, emptyText);
    }
    return (
      <div className="history-content" ref={this.listRefs[bucket]}>
        {events.map(this.renderTicketCard)}
        {this.renderInfiniteScroll(bucket)}
      </div>
    );
  };

  render() {
    const { __ } = this.props;

    return (
      <Loading transparent>
        <Layout showHamburger title={__('Tickets')} headerWithTitle color="transparent" scrollY={false}>
          <SwipableTabs
            history={this.props.location}
            tabs={[
              {
                label: __('Current'),
                tabContent: this.renderEventList(
                  'myEventsCurrent',
                  'No tickets for tonight',
                  'Tickets for events happening today will appear here',
                ),
              },
              {
                label: __('Upcoming'),
                tabContent: this.renderUpcomingList(
                  'myEventsUpcoming',
                  'No upcoming events',
                  "Once you book tickets, they'll show up here",
                ),
              },
              {
                label: __('History'),
                tabContent: this.renderUpcomingList(
                  'myEventsHistory',
                  'No past events',
                  "Events you've attended will be listed here",
                ),
              },
            ]}
          />
        </Layout>
      </Loading>
    );
  }
}

const stateToProps = (store) => {
  const {
    myEventsCurrent,
    myEventsCurrentPage,
    myEventsCurrentHasMore,
    myEventsUpcoming,
    myEventsUpcomingPage,
    myEventsUpcomingHasMore,
    myEventsHistory,
    myEventsHistoryPage,
    myEventsHistoryHasMore,
  } = store.orders;
  return {
    myEventsCurrent,
    myEventsCurrentPage,
    myEventsCurrentHasMore,
    myEventsUpcoming,
    myEventsUpcomingPage,
    myEventsUpcomingHasMore,
    myEventsHistory,
    myEventsHistoryPage,
    myEventsHistoryHasMore,
  };
};

export default connect(stateToProps)(withRouter(withTranslation(TicketsHistory)));
